import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Row, Col, Image, Container, Table, Modal, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { createCourse, updateCourse, getCourseDetails } from '../actions/courseActions'
import { createHole, updateHole, deleteHole } from '../actions/holeActions'
import { COURSE_CREATE_RESET, COURSE_UPDATE_RESET, COURSE_DETAILS_RESET } from '../constants/courseConstants'
import { HOLE_CREATE_RESET, HOLE_UPDATE_RESET } from '../constants/holeConstants'

const isMp4 = (url) => url && url.toLowerCase().endsWith('.mp4')

function CourseEditScreen() {
    const { id } = useParams()
    const isEditing = Boolean(id)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const populatedId = useRef(null)

    // Basic fields
    const [name, setName]           = useState('')
    const [location, setLocation]   = useState('')
    const [address, setAddress]     = useState('')
    const [website, setWebsite]     = useState('')

    // HTML description
    const [description, setDescription] = useState('')
    const [descPreview, setDescPreview] = useState(false)

    // Google Maps
    const [mapAddress, setMapAddress] = useState('')

    // Course stats
    const [par, setPar]             = useState('')
    const [holesCount, setHolesCount] = useState(18)
    const [rating, setRating]       = useState('')
    const [slope, setSlope]         = useState('')

    // Tee names
    const [tee4Name, setTee4Name]   = useState('')
    const [tee3Name, setTee3Name]   = useState('')
    const [tee2Name, setTee2Name]   = useState('')
    const [tee1Name, setTee1Name]   = useState('')

    // Images
    const [imageDesktop, setImageDesktop]     = useState(null)
    const [imageMobile, setImageMobile]       = useState(null)
    const [previewDesktop, setPreviewDesktop] = useState(null)
    const [previewMobile, setPreviewMobile]   = useState(null)
    const [desktopMimeType, setDesktopMimeType] = useState('')
    const [mobileMimeType, setMobileMimeType]   = useState('')

    // Hole modal fields
    const [showHoleModal, setShowHoleModal]           = useState(false)
    const [editingHole, setEditingHole]               = useState(null)
    const [holeNumber, setHoleNumber]                 = useState('')
    const [holePar, setHolePar]                       = useState(4)
    const [holeHandicap, setHoleHandicap]             = useState('')
    const [holeTee4, setHoleTee4]                     = useState('')
    const [holeTee3, setHoleTee3]                     = useState('')
    const [holeTee2, setHoleTee2]                     = useState('')
    const [holeTee1, setHoleTee1]                     = useState('')
    const [holeNotes, setHoleNotes]                   = useState('')
    const [holeImageDesktop, setHoleImageDesktop]     = useState(null)
    const [holeImageMobile, setHoleImageMobile]       = useState(null)
    const [holePreviewDesktop, setHolePreviewDesktop] = useState(null)
    const [holePreviewMobile, setHolePreviewMobile]   = useState(null)
    const [holeDesktopMimeType, setHoleDesktopMimeType] = useState('')
    const [holeMobileMimeType, setHoleMobileMimeType]   = useState('')

    const courseDetails = useSelector(state => state.courseDetails)
    const { loading: loadingDetails, error: errorDetails, course } = courseDetails

    const courseCreate = useSelector(state => state.courseCreate)
    const { loading: loadingCreate, error: errorCreate, success: successCreate } = courseCreate

    const courseUpdate = useSelector(state => state.courseUpdate)
    const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = courseUpdate

    const holeCreate = useSelector(state => state.holeCreate)
    const { loading: loadingHoleCreate, error: errorHoleCreate, success: successHoleCreate } = holeCreate

    const holeUpdate = useSelector(state => state.holeUpdate)
    const { loading: loadingHoleUpdate, error: errorHoleUpdate, success: successHoleUpdate } = holeUpdate

    const holeDelete = useSelector(state => state.holeDelete)
    const { loading: loadingHoleDelete, error: errorHoleDelete, success: successHoleDelete } = holeDelete

    // 1. Fetch on mount
    useEffect(() => {
        if (isEditing) {
            dispatch(getCourseDetails(id))
        }
        return () => { dispatch({ type: COURSE_DETAILS_RESET }) }
    }, [id])

    // 2. Populate form when data arrives (only first time for this course)
    useEffect(() => {
        if (course && course.id && course.id !== populatedId.current) {
            populatedId.current = course.id
            setName(course.name || '')
            setLocation(course.location || '')
            setAddress(course.address || '')
            setMapAddress(course.address || '')
            setWebsite(course.website || '')
            setDescription(course.description || '')
            setPar(course.par || '')
            setHolesCount(course.holes_count || 18)
            setRating(course.rating || '')
            setSlope(course.slope || '')
            setTee4Name(course.tee_4_name || '')
            setTee3Name(course.tee_3_name || '')
            setTee2Name(course.tee_2_name || '')
            setTee1Name(course.tee_1_name || '')
            setPreviewDesktop(course.image_desktop || null)
            setPreviewMobile(course.image_mobile || null)
            if (course.image_desktop && isMp4(course.image_desktop)) setDesktopMimeType('video/mp4')
            if (course.image_mobile  && isMp4(course.image_mobile))  setMobileMimeType('video/mp4')
        }
    }, [course])

    // 3. Navigate on success
    useEffect(() => {
        if (successCreate || successUpdate) {
            dispatch({ type: COURSE_CREATE_RESET })
            dispatch({ type: COURSE_UPDATE_RESET })
            navigate('/admin/courses')
        }
    }, [successCreate, successUpdate])

    // 4. Close modal and re-fetch course after hole operations
    useEffect(() => {
        if (successHoleCreate || successHoleUpdate || successHoleDelete) {
            dispatch({ type: HOLE_CREATE_RESET })
            dispatch({ type: HOLE_UPDATE_RESET })
            setShowHoleModal(false)
            if (id) dispatch(getCourseDetails(id))
        }
    }, [successHoleCreate, successHoleUpdate, successHoleDelete])

    const handleFileChange = (e, type) => {
        const file = e.target.files[0]
        if (!file) return
        const url = URL.createObjectURL(file)
        if (type === 'desktop') {
            setImageDesktop(file)
            setPreviewDesktop(url)
            setDesktopMimeType(file.type)
        } else {
            setImageMobile(file)
            setPreviewMobile(url)
            setMobileMimeType(file.type)
        }
    }

    const submitHandler = (e) => {
        e.preventDefault()
        const formData = new FormData()
        formData.append('name', name)
        formData.append('location', location)
        formData.append('address', address)
        formData.append('website', website)
        formData.append('description', description)
        formData.append('par', par)
        formData.append('holes_count', holesCount)
        formData.append('rating', rating)
        formData.append('slope', slope)
        formData.append('tee_4_name', tee4Name)
        formData.append('tee_3_name', tee3Name)
        formData.append('tee_2_name', tee2Name)
        formData.append('tee_1_name', tee1Name)
        if (imageDesktop) formData.append('image_desktop', imageDesktop)
        if (imageMobile)  formData.append('image_mobile', imageMobile)

        isEditing ? dispatch(updateCourse(id, formData)) : dispatch(createCourse(formData))
    }

    const openHoleModal = (hole = null) => {
        setEditingHole(hole)
        setHoleImageDesktop(null)
        setHoleImageMobile(null)
        if (hole) {
            setHoleNumber(hole.number)
            setHolePar(hole.par)
            setHoleHandicap(hole.handicap ?? '')
            setHoleTee4(hole.tee_4_yards ?? '')
            setHoleTee3(hole.tee_3_yards ?? '')
            setHoleTee2(hole.tee_2_yards ?? '')
            setHoleTee1(hole.tee_1_yards ?? '')
            setHoleNotes(hole.notes || '')
            setHolePreviewDesktop(hole.image_desktop || null)
            setHolePreviewMobile(hole.image_mobile || null)
            setHoleDesktopMimeType(isMp4(hole.image_desktop) ? 'video/mp4' : '')
            setHoleMobileMimeType(isMp4(hole.image_mobile) ? 'video/mp4' : '')
        } else {
            setHoleNumber('')
            setHolePar(4)
            setHoleHandicap('')
            setHoleTee4(''); setHoleTee3(''); setHoleTee2(''); setHoleTee1('')
            setHoleNotes('')
            setHolePreviewDesktop(null); setHolePreviewMobile(null)
            setHoleDesktopMimeType(''); setHoleMobileMimeType('')
        }
        setShowHoleModal(true)
    }

    const holeSubmitHandler = (e) => {
        e.preventDefault()
        const formData = new FormData()
        formData.append('number', holeNumber)
        formData.append('par', holePar)
        if (holeHandicap !== '') formData.append('handicap', holeHandicap)
        if (holeTee4 !== '') formData.append('tee_4_yards', holeTee4)
        if (holeTee3 !== '') formData.append('tee_3_yards', holeTee3)
        if (holeTee2 !== '') formData.append('tee_2_yards', holeTee2)
        if (holeTee1 !== '') formData.append('tee_1_yards', holeTee1)
        if (holeNotes) formData.append('notes', holeNotes)
        if (holeImageDesktop) formData.append('image_desktop', holeImageDesktop)
        if (holeImageMobile)  formData.append('image_mobile', holeImageMobile)
        if (editingHole) {
            dispatch(updateHole(editingHole.id, formData))
        } else {
            dispatch(createHole(id, formData))
        }
    }

    const deleteHoleHandler = (holeId) => {
        if (window.confirm('Delete this hole?')) {
            dispatch(deleteHole(holeId))
        }
    }

    const handleHoleFile = (e, type) => {
        const file = e.target.files[0]
        if (!file) return
        const url = URL.createObjectURL(file)
        if (type === 'desktop') {
            setHoleImageDesktop(file); setHolePreviewDesktop(url); setHoleDesktopMimeType(file.type)
        } else {
            setHoleImageMobile(file); setHolePreviewMobile(url); setHoleMobileMimeType(file.type)
        }
    }

    const mediaPreview = (preview, mimeType, label) => {
        if (!preview) return null
        return mimeType === 'video/mp4'
            ? <video src={preview} style={{ maxHeight: '120px', maxWidth: '100%' }} controls muted className='mb-2 d-block' />
            : <Image src={preview} alt={label} fluid style={{ maxHeight: '120px', objectFit: 'contain' }} className='mb-2 d-block' />
    }

    const holes = course?.holes || []
    const t4 = tee4Name || 'Tee 4'
    const t3 = tee3Name || 'Tee 3'
    const t2 = tee2Name || 'Tee 2'
    const t1 = tee1Name || 'Tee 1'

    return (
        <Container fluid='lg'>
            <Row className='align-items-center mb-3'>
                <Col><h2>{isEditing ? 'Edit Course' : 'Add Course'}</h2></Col>
                <Col className='text-end'>
                    <Button variant='outline-secondary' size='sm' onClick={() => navigate('/admin/courses')}>
                        Cancel
                    </Button>
                </Col>
            </Row>

            {(loadingDetails || loadingCreate || loadingUpdate) && <Loader />}
            {(errorDetails || errorCreate || errorUpdate) && (
                <Message variant='danger'>{errorDetails || errorCreate || errorUpdate}</Message>
            )}

            <Form onSubmit={submitHandler}>

                {/* ── Basic Info ─────────────────────────────────── */}
                <Row className='mb-3'>
                    <Col md={8}>
                        <Form.Group controlId='name'>
                            <Form.Label>Course Name</Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='e.g. Pebble Beach Golf Links'
                                value={name}
                                onChange={e => setName(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId='location'>
                            <Form.Label>Location / City</Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='e.g. Pebble Beach, CA'
                                value={location}
                                onChange={e => setLocation(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className='mb-3' controlId='website'>
                    <Form.Label>Website <span className='text-muted'>(optional)</span></Form.Label>
                    <Form.Control
                        type='url'
                        placeholder='https://...'
                        value={website}
                        onChange={e => setWebsite(e.target.value)}
                    />
                </Form.Group>

                {/* ── Address + Google Maps ───────────────────────── */}
                <Form.Group className='mb-2' controlId='address'>
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                        type='text'
                        placeholder='123 Golf Club Dr, City, State ZIP'
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        onBlur={e => setMapAddress(e.target.value)}
                    />
                    <Form.Text className='text-muted'>Map updates when you leave the field</Form.Text>
                </Form.Group>

                {mapAddress && (
                    <div className='mb-4 rounded overflow-hidden border'>
                        <iframe
                            title='course-map'
                            width='100%'
                            height='220'
                            style={{ border: 0, display: 'block' }}
                            src={`https://maps.google.com/maps?q=${encodeURIComponent(mapAddress)}&output=embed`}
                            allowFullScreen
                            loading='lazy'
                        />
                    </div>
                )}

                {/* ── HTML Description ───────────────────────────── */}
                <Form.Group className='mb-4' controlId='description'>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                        <Form.Label className='mb-0'>Description</Form.Label>
                        <Button
                            variant={descPreview ? 'outline-secondary' : 'outline-primary'}
                            size='sm'
                            type='button'
                            onClick={() => setDescPreview(p => !p)}
                        >
                            {descPreview
                                ? <><i className='fa-solid fa-code me-1'></i>Edit HTML</>
                                : <><i className='fa-solid fa-eye me-1'></i>Preview</>
                            }
                        </Button>
                    </div>

                    {descPreview ? (
                        <div
                            className='border rounded p-3'
                            style={{ minHeight: '160px', background: '#fff' }}
                            dangerouslySetInnerHTML={{ __html: description || '<p class="text-muted">Nothing to preview yet.</p>' }}
                        />
                    ) : (
                        <Form.Control
                            as='textarea'
                            rows={7}
                            placeholder='<p>Enter HTML description...</p>'
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                        />
                    )}
                </Form.Group>

                {/* ── Course Stats ───────────────────────────────── */}
                <Row className='mb-3'>
                    <Col sm={3}>
                        <Form.Group controlId='par'>
                            <Form.Label>Par</Form.Label>
                            <Form.Control
                                type='number'
                                placeholder='72'
                                value={par}
                                onChange={e => setPar(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col sm={3}>
                        <Form.Group controlId='holesCount'>
                            <Form.Label>Holes</Form.Label>
                            <Form.Control
                                type='number'
                                placeholder='18'
                                value={holesCount}
                                onChange={e => setHolesCount(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col sm={3}>
                        <Form.Group controlId='rating'>
                            <Form.Label>Rating</Form.Label>
                            <Form.Control
                                type='number'
                                step='0.1'
                                placeholder='74.5'
                                value={rating}
                                onChange={e => setRating(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col sm={3}>
                        <Form.Group controlId='slope'>
                            <Form.Label>Slope</Form.Label>
                            <Form.Control
                                type='number'
                                placeholder='135'
                                value={slope}
                                onChange={e => setSlope(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* ── Tee Names ──────────────────────────────────── */}
                <p className='fw-semibold mb-2'>Tee Names <span className='text-muted fw-normal'>(optional)</span></p>
                <Row className='mb-4'>
                    {[
                        { label: 'Tee 4 (Back)',   val: tee4Name, set: setTee4Name },
                        { label: 'Tee 3',          val: tee3Name, set: setTee3Name },
                        { label: 'Tee 2',          val: tee2Name, set: setTee2Name },
                        { label: 'Tee 1 (Forward)',val: tee1Name, set: setTee1Name },
                    ].map((tee, i) => (
                        <Col sm={3} key={i}>
                            <Form.Group>
                                <Form.Label>{tee.label}</Form.Label>
                                <Form.Control
                                    type='text'
                                    placeholder='e.g. Black'
                                    value={tee.val}
                                    onChange={e => tee.set(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    ))}
                </Row>

                {/* ── Images ─────────────────────────────────────── */}
                <Row className='mb-4'>
                    <Col md={6}>
                        <Form.Group controlId='imageDesktop'>
                            <Form.Label>Desktop Image / Video</Form.Label>
                            {mediaPreview(previewDesktop, desktopMimeType, 'desktop preview')}
                            <Form.Control
                                type='file'
                                accept='image/*,video/mp4,.gif'
                                onChange={e => handleFileChange(e, 'desktop')}
                            />
                            <Form.Text className='text-muted'>Supports images, GIF, or MP4</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId='imageMobile'>
                            <Form.Label>Mobile Image / Video</Form.Label>
                            {mediaPreview(previewMobile, mobileMimeType, 'mobile preview')}
                            <Form.Control
                                type='file'
                                accept='image/*,video/mp4,.gif'
                                onChange={e => handleFileChange(e, 'mobile')}
                            />
                            <Form.Text className='text-muted'>Supports images, GIF, or MP4</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                {/* ── Actions ────────────────────────────────────── */}
                <div className='d-flex gap-2 mb-3'>
                    <Button type='submit' variant='dark'>
                        {isEditing ? 'Save Changes' : 'Create Course'}
                    </Button>
                    <Button variant='outline-secondary' type='button' onClick={() => navigate('/admin/courses')}>
                        Cancel
                    </Button>
                </div>

            </Form>

            {/* ── Holes Section ──────────────────────────────── */}
            {isEditing ? (
                <>
                    <hr className='my-4' />
                    <Row className='align-items-center mb-3'>
                        <Col>
                            <h4>Holes <Badge bg='secondary'>{holes.length}</Badge></h4>
                        </Col>
                        <Col className='text-end'>
                            <Button variant='dark' size='sm' onClick={() => openHoleModal()}>
                                <i className='fa-solid fa-plus me-1'></i> Add Hole
                            </Button>
                        </Col>
                    </Row>

                    {(errorHoleCreate || errorHoleUpdate || errorHoleDelete) && (
                        <Message variant='danger'>{errorHoleCreate || errorHoleUpdate || errorHoleDelete}</Message>
                    )}

                    {holes.length === 0 ? (
                        <p className='text-muted'>No holes added yet. Click Add Hole to get started.</p>
                    ) : (
                        <Table striped hover responsive size='sm' className='mb-5'>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Par</th>
                                    <th>Hcp</th>
                                    <th>{t4}</th>
                                    <th>{t3}</th>
                                    <th>{t2}</th>
                                    <th>{t1}</th>
                                    <th>Photo</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {holes.map(hole => (
                                    <tr key={hole.id}>
                                        <td className='fw-bold'>{hole.number}</td>
                                        <td>{hole.par}</td>
                                        <td>{hole.handicap ?? '–'}</td>
                                        <td>{hole.tee_4_yards ?? '–'}</td>
                                        <td>{hole.tee_3_yards ?? '–'}</td>
                                        <td>{hole.tee_2_yards ?? '–'}</td>
                                        <td>{hole.tee_1_yards ?? '–'}</td>
                                        <td>
                                            {hole.image_desktop && (
                                                <img
                                                    src={hole.image_desktop}
                                                    alt=''
                                                    style={{ height: '32px', width: '48px', objectFit: 'cover', borderRadius: '3px' }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <Button
                                                variant='outline-secondary'
                                                size='sm'
                                                className='me-1'
                                                onClick={() => openHoleModal(hole)}
                                            >
                                                <i className='fa-solid fa-pen'></i>
                                            </Button>
                                            <Button
                                                variant='outline-danger'
                                                size='sm'
                                                onClick={() => deleteHoleHandler(hole.id)}
                                            >
                                                <i className='fa-solid fa-trash'></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </>
            ) : (
                <p className='text-muted mt-2'>
                    <small>Save the course first to start adding holes.</small>
                </p>
            )}

            {/* ── Hole Modal ─────────────────────────────────── */}
            <Modal show={showHoleModal} onHide={() => setShowHoleModal(false)} size='lg'>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingHole ? `Edit Hole ${editingHole.number}` : 'Add Hole'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(loadingHoleCreate || loadingHoleUpdate) && <Loader />}
                    <Form id='hole-form' onSubmit={holeSubmitHandler}>
                        <Row className='mb-3'>
                            <Col sm={4}>
                                <Form.Group>
                                    <Form.Label>Hole #</Form.Label>
                                    <Form.Control
                                        type='number'
                                        min='1'
                                        max='18'
                                        value={holeNumber}
                                        onChange={e => setHoleNumber(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={4}>
                                <Form.Group>
                                    <Form.Label>Par</Form.Label>
                                    <Form.Control
                                        type='number'
                                        min='3'
                                        max='6'
                                        value={holePar}
                                        onChange={e => setHolePar(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={4}>
                                <Form.Group>
                                    <Form.Label>Handicap</Form.Label>
                                    <Form.Control
                                        type='number'
                                        min='1'
                                        max='18'
                                        value={holeHandicap}
                                        onChange={e => setHoleHandicap(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className='mb-3'>
                            {[
                                { label: `${t4} (yds)`, val: holeTee4, set: setHoleTee4 },
                                { label: `${t3} (yds)`, val: holeTee3, set: setHoleTee3 },
                                { label: `${t2} (yds)`, val: holeTee2, set: setHoleTee2 },
                                { label: `${t1} (yds)`, val: holeTee1, set: setHoleTee1 },
                            ].map((tee, i) => (
                                <Col sm={3} key={i}>
                                    <Form.Group>
                                        <Form.Label>{tee.label}</Form.Label>
                                        <Form.Control
                                            type='number'
                                            min='0'
                                            value={tee.val}
                                            onChange={e => tee.set(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>

                        <Form.Group className='mb-3'>
                            <Form.Label>Notes <span className='text-muted'>(optional)</span></Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={2}
                                value={holeNotes}
                                onChange={e => setHoleNotes(e.target.value)}
                                placeholder='Water hazard on right, bunker at 200 yards...'
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Desktop Image / Video</Form.Label>
                                    {mediaPreview(holePreviewDesktop, holeDesktopMimeType, 'hole desktop')}
                                    <Form.Control
                                        type='file'
                                        accept='image/*,video/mp4,.gif'
                                        onChange={e => handleHoleFile(e, 'desktop')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Mobile Image / Video</Form.Label>
                                    {mediaPreview(holePreviewMobile, holeMobileMimeType, 'hole mobile')}
                                    <Form.Control
                                        type='file'
                                        accept='image/*,video/mp4,.gif'
                                        onChange={e => handleHoleFile(e, 'mobile')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowHoleModal(false)}>Cancel</Button>
                    <Button variant='dark' type='submit' form='hole-form'>
                        {editingHole ? 'Save Hole' : 'Add Hole'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    )
}

export default CourseEditScreen
